import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, permission: 'dashboard:view' },
  },
  {
    path: '/system/users',
    name: 'system-users',
    component: () => import('@/views/system/UsersView.vue'),
    meta: { requiresAuth: true, permission: 'system:user:view' },
  },
  {
    path: '/system/roles',
    name: 'system-roles',
    component: () => import('@/views/system/RolesView.vue'),
    meta: { requiresAuth: true, permission: 'system:role:view' },
  },
  {
    path: '/system/menus',
    name: 'system-menus',
    component: () => import('@/views/system/MenusView.vue'),
    meta: { requiresAuth: true, permission: 'system:menu:view' },
  },
  {
    path: '/system/permissions',
    name: 'system-permissions',
    component: () => import('@/views/system/PermissionsView.vue'),
    meta: { requiresAuth: true, permission: 'system:permission:view' },
  },
  {
    path: '/system/sessions',
    name: 'system-sessions',
    component: () => import('@/views/system/SessionsView.vue'),
    meta: { requiresAuth: true, permission: 'system:session:view' },
  },
  {
    path: '/system/funcs',
    name: 'system-funcs',
    component: () => import('@/views/system/FuncsView.vue'),
    meta: { requiresAuth: true, permission: 'system:func:view' },
  },
  {
    path: '/system/ip-blocks',
    name: 'system-ip-blocks',
    component: () => import('@/views/system/IpBlocksView.vue'),
    meta: { requiresAuth: true, permission: 'system:ip_block:view' },
  },
  {
    path: '/system/code-configs',
    name: 'system-code-configs',
    component: () => import('@/views/system/CodeConfigsView.vue'),
    meta: { requiresAuth: true, permission: 'system:code_config:view' },
  },
  {
    path: '/reward/my-points',
    name: 'reward-my-points',
    component: () => import('@/views/system/UserPointsView.vue'),
    meta: {
      requiresAuth: true,
      // self page; manage also allowed so admin can open from menu/search
      anyPermission: ['reward:my_point:view', 'system:point_manage:view'],
      pointsMode: 'self',
    },
  },
  {
    path: '/reward/point-details',
    name: 'reward-point-details',
    component: () => import('@/views/system/PointDetailsView.vue'),
    meta: {
      requiresAuth: true,
      anyPermission: ['reward:point_detail:view', 'system:point_manage:view'],
      pointsMode: 'self',
    },
  },
  {
    path: '/system/point-manage',
    name: 'system-point-manage',
    component: () => import('@/views/system/UserPointsView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'system:point_manage:view',
      pointsMode: 'manage',
    },
  },
  {
    path: '/system/reward-clawback',
    name: 'system-reward-clawback',
    component: () => import('@/views/system/RewardClawbackView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'system:reward_clawback:view',
    },
  },

  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reward/exchange',
    name: 'reward-exchange',
    component: () => import('@/views/reward/RewardExchangeView.vue'),
    meta: { requiresAuth: true, permission: 'reward:exchange:view' },
  },
  {
    path: '/system/point-products',
    name: 'system-point-products',
    component: () => import('@/views/system/PointProductsView.vue'),
    meta: { requiresAuth: true, permission: 'system:point_product:view' },
  },
  {
    path: '/system/point-orders',
    name: 'system-point-orders',
    component: () => import('@/views/system/PointOrdersView.vue'),
    meta: { requiresAuth: true, permission: 'system:point_order:view' },
  },
  {
    path: '/system/redeem-codes',
    name: 'system-redeem-codes',
    component: () => import('@/views/system/RedeemCodesView.vue'),
    meta: { requiresAuth: true, permission: 'system:redeem_code:view' },
  },
  {
    path: '/reward/my-redeem-codes',
    name: 'reward-my-redeem-codes',
    component: () => import('@/views/reward/MyRedeemCodesView.vue'),
    meta: { requiresAuth: true, permission: 'reward:redeem:view' },
  },
  {
    path: '/system/point-scenes',
    name: 'system-point-scenes',
    component: () => import('@/views/system/PointScenesView.vue'),
    meta: { requiresAuth: true, permission: 'system:point_scene:view' },
  },
  {
    path: '/system/checkin-plans',
    name: 'system-checkin-plans',
    component: () => import('@/views/system/CheckinPlansView.vue'),
    meta: { requiresAuth: true, permission: 'system:checkin:view' },
  },
  {
    path: '/system/checkin-logs',
    name: 'system-checkin-logs',
    component: () => import('@/views/system/CheckinLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:checkin_log:view' },
  },
  {
    path: '/reward/checkin',
    name: 'reward-checkin',
    component: () => import('@/views/reward/CheckinView.vue'),
    meta: { requiresAuth: true, permission: 'reward:checkin:view' },
  },
  {
    path: '/system/quiz-banks',
    name: 'system-quiz-banks',
    component: () => import('@/views/system/QuizBanksView.vue'),
    meta: { requiresAuth: true, permission: 'system:quiz_bank:view' },
  },
  {
    path: '/system/quiz-activities',
    name: 'system-quiz-activities',
    component: () => import('@/views/system/QuizActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:quiz_activity:view' },
  },
  {
    path: '/system/quiz-logs',
    name: 'system-quiz-logs',
    component: () => import('@/views/system/QuizLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:quiz_log:view' },
  },
  {
    path: '/activity/quiz',
    name: 'activity-quiz',
    component: () => import('@/views/activity/QuizView.vue'),
    meta: { requiresAuth: true, permission: 'activity:quiz:view' },
  },
  {
    path: '/activity/quiz-history',
    name: 'activity-quiz-history',
    component: () => import('@/views/activity/QuizHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:quiz:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/quiz',
    },
  },
  {
    path: '/system/lottery-activities',
    name: 'system-lottery-activities',
    component: () => import('@/views/system/LotteryActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:lottery:view' },
  },
  {
    path: '/system/lottery-logs',
    name: 'system-lottery-logs',
    component: () => import('@/views/system/LotteryLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:lottery_log:view' },
  },
  {
    path: '/system/lottery-tickets',
    name: 'system-lottery-tickets',
    component: () => import('@/views/system/LotteryTicketsView.vue'),
    meta: { requiresAuth: true, permission: 'system:lottery_ticket:view' },
  },
  {
    path: '/reward/lottery-ticket-details',
    name: 'reward-lottery-ticket-details',
    component: () => import('@/views/system/LotteryTicketDetailsView.vue'),
    meta: {
      requiresAuth: true,
      anyPermission: [
        'reward:lottery_ticket_detail:view',
        'system:lottery_ticket:view',
      ],
    },
  },
  {
    path: '/activity/lottery',
    name: 'activity-lottery',
    component: () => import('@/views/activity/LotteryView.vue'),
    meta: { requiresAuth: true, permission: 'activity:lottery:view' },
  },
  {
    path: '/activity/lottery-history',
    name: 'activity-lottery-history',
    component: () => import('@/views/activity/LotteryHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:lottery:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/lottery',
    },
  },
  {
    path: '/system/learn-courses',
    name: 'system-learn-courses',
    component: () => import('@/views/system/LearnCoursesView.vue'),
    meta: { requiresAuth: true, permission: 'system:learn:view' },
  },
  {
    path: '/system/learn-logs',
    name: 'system-learn-logs',
    component: () => import('@/views/system/LearnLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:learn_log:view' },
  },
  {
    path: '/activity/learn',
    name: 'activity-learn',
    component: () => import('@/views/activity/LearnView.vue'),
    meta: { requiresAuth: true, permission: 'activity:learn:view' },
  },
  {
    path: '/activity/learn-history',
    name: 'activity-learn-history',
    component: () => import('@/views/activity/LearnHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:learn:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/learn',
    },
  },
  {
    path: '/system/wuxing-activities',
    name: 'system-wuxing-activities',
    component: () => import('@/views/system/WuxingActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:wuxing:view' },
  },
  {
    path: '/system/wuxing-logs',
    name: 'system-wuxing-logs',
    component: () => import('@/views/system/WuxingLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:wuxing_log:view' },
  },
  {
    path: '/activity/wuxing',
    name: 'activity-wuxing',
    component: () => import('@/views/activity/WuxingView.vue'),
    meta: { requiresAuth: true, permission: 'activity:wuxing:view' },
  },
  {
    path: '/activity/wuxing-history',
    name: 'activity-wuxing-history',
    component: () => import('@/views/activity/WuxingHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:wuxing:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/wuxing',
    },
  },
  {
    path: '/tools/ski-auth',
    name: 'tools-ski-auth',
    component: () => import('@/views/tools/SkiAuthProbeView.vue'),
    meta: { requiresAuth: true, permission: 'tool:ski_auth:view' },
  },
  {
    path: '/media/:platform',
    name: 'media-platform',
    component: () => import('@/views/media/MediaView.vue'),
    meta: { requiresAuth: true, permission: 'media:view' },
  },
  {
    path: '/media/:platform/:id',
    name: 'media-detail',
    component: () => import('@/views/media/MediaDetailView.vue'),
    meta: { requiresAuth: true, permission: 'media:view' },
  },
  {
    path: '/board/points',
    name: 'board-points',
    component: () => import('@/views/board/BoardPointsView.vue'),
    meta: { requiresAuth: true, permission: 'board:points:view' },
  },
  {
    path: '/board/week',
    name: 'board-week',
    component: () => import('@/views/board/BoardWeekView.vue'),
    meta: { requiresAuth: true, permission: 'board:week:view' },
  },
  {
    path: '/board/growth',
    name: 'board-growth',
    component: () => import('@/views/board/GrowthBoardView.vue'),
    meta: { requiresAuth: true, permission: 'board:growth:view' },
  },
  {
    path: '/reward/growth',
    name: 'reward-growth',
    component: () => import('@/views/reward/GrowthView.vue'),
    meta: { requiresAuth: true, permission: 'growth:my:view' },
  },
  {
    path: '/reward/growth-details',
    name: 'reward-growth-details',
    component: () => import('@/views/reward/GrowthDetailsView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'growth:my:view',
      // 从属页面：复用「我的成长」的菜单高亮（明细页不单独占菜单位）
      activeMenu: '/reward/growth',
    },
  },
  {
    path: '/system/growth-settings',
    name: 'system-growth-settings',
    component: () => import('@/views/system/GrowthSettingsView.vue'),
    meta: { requiresAuth: true, permission: 'system:growth:view' },
  },
  {
    path: '/system/growth-realms',
    name: 'system-growth-realms',
    component: () => import('@/views/system/GrowthRealmsView.vue'),
    meta: { requiresAuth: true, permission: 'system:growth:view' },
  },
  {
    path: '/system/growth-materials',
    name: 'system-growth-materials',
    component: () => import('@/views/system/GrowthMaterialsView.vue'),
    meta: { requiresAuth: true, permission: 'system:growth:view' },
  },
  {
    path: '/system/growth-chars',
    name: 'system-growth-chars',
    component: () => import('@/views/system/GrowthCharsView.vue'),
    meta: { requiresAuth: true, permission: 'system:growth:view' },
  },
  {
    path: '/system/growth-logs',
    name: 'system-growth-logs',
    component: () => import('@/views/system/GrowthLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:growth_log:view' },
  },
  {
    path: '/system/growth-details',
    name: 'system-growth-details',
    component: () => import('@/views/system/GrowthDetailsView.vue'),
    meta: { requiresAuth: true, permission: 'system:growth_detail:view' },
  },
  {
    path: '/system/growth-drops',
    name: 'system-growth-drops',
    component: () => import('@/views/system/GrowthDropsView.vue'),
    meta: { requiresAuth: true, permission: 'system:growth_drop:view' },
  },
  {
    path: '/system/growth-drop-logs',
    name: 'system-growth-drop-logs',
    component: () => import('@/views/system/GrowthDropLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:growth_drop_log:view' },
  },
  {
    path: '/system/blind-boxes',
    name: 'system-blind-boxes',
    component: () => import('@/views/system/BlindBoxesView.vue'),
    meta: { requiresAuth: true, permission: 'system:blind_box:view' },
  },
  {
    path: '/system/blind-box-logs',
    name: 'system-blind-box-logs',
    component: () => import('@/views/system/BlindBoxLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:blind_box_log:view' },
  },
  {
    path: '/reward/blind-boxes',
    name: 'reward-blind-boxes',
    component: () => import('@/views/reward/BlindBoxesView.vue'),
    meta: { requiresAuth: true, permission: 'reward:blind_box:view' },
  },
  {
    path: '/system/board-week',
    name: 'system-board-week',
    component: () => import('@/views/system/BoardWeekView.vue'),
    meta: { requiresAuth: true, permission: 'system:board_week:view' },
  },
  {
    path: '/system/board-spend',
    name: 'system-board-spend',
    component: () => import('@/views/system/BoardSpendView.vue'),
    meta: { requiresAuth: true, permission: 'system:board_spend:view' },
  },
  {
    path: '/system/tasks',
    name: 'system-tasks',
    component: () => import('@/views/system/TasksView.vue'),
    meta: { requiresAuth: true, permission: 'system:task:view' },
  },
  {
    path: '/reward/tasks',
    name: 'reward-tasks',
    component: () => import('@/views/reward/TasksView.vue'),
    meta: { requiresAuth: true, permission: 'reward:task:view' },
  },
  {
    path: '/system/raffle-activities',
    name: 'system-raffle-activities',
    component: () => import('@/views/system/RaffleActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:raffle:view' },
  },
  {
    path: '/system/raffle-logs',
    name: 'system-raffle-logs',
    component: () => import('@/views/system/RaffleLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:raffle_log:view' },
  },
  {
    path: '/activity/raffle',
    name: 'activity-raffle',
    component: () => import('@/views/activity/RaffleView.vue'),
    meta: { requiresAuth: true, permission: 'activity:raffle:view' },
  },
  {
    path: '/activity/raffle-history',
    name: 'activity-raffle-history',
    component: () => import('@/views/activity/RaffleHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:raffle:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/raffle',
    },
  },
  {
    path: '/system/seckill-activities',
    name: 'system-seckill-activities',
    component: () => import('@/views/system/SeckillActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:seckill:view' },
  },
  {
    path: '/system/seckill-logs',
    name: 'system-seckill-logs',
    component: () => import('@/views/system/SeckillLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:seckill_log:view' },
  },
  {
    path: '/activity/seckill',
    name: 'activity-seckill',
    component: () => import('@/views/activity/SeckillView.vue'),
    meta: { requiresAuth: true, permission: 'activity:seckill:view' },
  },
  {
    path: '/activity/seckill-history',
    name: 'activity-seckill-history',
    component: () => import('@/views/activity/SeckillHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:seckill:view',
      // 从属页面：复用主活动页的菜单高亮
      activeMenu: '/activity/seckill',
    },
  },
  {
    path: '/system/camp-activities',
    name: 'system-camp-activities',
    component: () => import('@/views/system/CampActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:camp:view' },
  },
  {
    path: '/system/camp-logs',
    name: 'system-camp-logs',
    component: () => import('@/views/system/CampLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:camp_log:view' },
  },
  {
    path: '/activity/camp',
    name: 'activity-camp',
    component: () => import('@/views/activity/CampView.vue'),
    meta: { requiresAuth: true, permission: 'activity:camp:view' },
  },
  {
    path: '/activity/camp-history',
    name: 'activity-camp-history',
    component: () => import('@/views/activity/CampHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:camp:view',
      // 从属页面：复用主活动页的菜单高亮
      activeMenu: '/activity/camp',
    },
  },
  {
    path: '/system/card-expedition',
    name: 'system-card-expedition',
    component: () => import('@/views/system/CardExpeditionView.vue'),
    meta: { requiresAuth: true, permission: 'system:card_expedition:view' },
  },
  {
    path: '/system/card-expedition-logs',
    name: 'system-card-expedition-logs',
    component: () => import('@/views/system/CardExpeditionLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:card_expedition_log:view' },
  },
  {
    path: '/activity/card-expedition',
    name: 'activity-card-expedition',
    component: () => import('@/views/activity/CardExpeditionView.vue'),
    meta: { requiresAuth: true, permission: 'activity:card_expedition:view' },
  },
  {
    path: '/activity/card-expedition-history',
    name: 'activity-card-expedition-history',
    component: () => import('@/views/activity/CardExpeditionHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:card_expedition:view',
      activeMenu: '/activity/card-expedition',
    },
  },
  {
    path: '/system/fortune-activities',
    name: 'system-fortune-activities',
    component: () => import('@/views/system/FortuneActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:fortune:view' },
  },
  {
    path: '/system/fortune-logs',
    name: 'system-fortune-logs',
    component: () => import('@/views/system/FortuneLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:fortune_log:view' },
  },
  {
    path: '/activity/fortune',
    name: 'activity-fortune',
    component: () => import('@/views/activity/FortuneView.vue'),
    meta: { requiresAuth: true, permission: 'activity:fortune:view' },
  },
  {
    path: '/activity/fortune-history',
    name: 'activity-fortune-history',
    component: () => import('@/views/activity/FortuneHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:fortune:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/fortune',
    },
  },
  {
    path: '/system/dice-activities',
    name: 'system-dice-activities',
    component: () => import('@/views/system/DiceActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:dice:view' },
  },
  {
    path: '/system/dice-logs',
    name: 'system-dice-logs',
    component: () => import('@/views/system/DiceLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:dice_log:view' },
  },
  {
    path: '/activity/dice',
    name: 'activity-dice',
    component: () => import('@/views/activity/DiceView.vue'),
    meta: { requiresAuth: true, permission: 'activity:dice:view' },
  },
  {
    path: '/activity/dice-history',
    name: 'activity-dice-history',
    component: () => import('@/views/activity/DiceHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:dice:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/dice',
    },
  },
  {
    path: '/system/flip-activities',
    name: 'system-flip-activities',
    component: () => import('@/views/system/FlipActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:flip:view' },
  },
  {
    path: '/system/flip-logs',
    name: 'system-flip-logs',
    component: () => import('@/views/system/FlipLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:flip_log:view' },
  },
  {
    path: '/activity/flip',
    name: 'activity-flip',
    component: () => import('@/views/activity/FlipView.vue'),
    meta: { requiresAuth: true, permission: 'activity:flip:view' },
  },
  {
    path: '/activity/flip-history',
    name: 'activity-flip-history',
    component: () => import('@/views/activity/FlipHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:flip:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/flip',
    },
  },
  {
    path: '/system/collect-activities',
    name: 'system-collect-activities',
    component: () => import('@/views/system/CollectActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:collect:view' },
  },
  {
    path: '/system/collect-logs',
    name: 'system-collect-logs',
    component: () => import('@/views/system/CollectLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:collect_log:view' },
  },
  {
    path: '/activity/collect',
    name: 'activity-collect',
    component: () => import('@/views/activity/CollectView.vue'),
    meta: { requiresAuth: true, permission: 'activity:collect:view' },
  },
  {
    path: '/activity/collect-history',
    name: 'activity-collect-history',
    component: () => import('@/views/activity/CollectHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:collect:view',
      // 从属页面：复用主活动页的菜单高亮（历史页不单独占菜单位）
      activeMenu: '/activity/collect',
    },
  },
  {
    path: '/system/boss-activities',
    name: 'system-boss-activities',
    component: () => import('@/views/system/BossActivitiesView.vue'),
    meta: { requiresAuth: true, permission: 'system:boss:view' },
  },
  {
    path: '/system/game-content/:section?',
    name: 'system-game-content',
    component: () => import('@/views/system/GameContentView.vue'),
    meta: { requiresAuth: true, permission: 'system:game:view' },
  },
  {
    path: '/system/boss-logs',
    name: 'system-boss-logs',
    component: () => import('@/views/system/BossLogsView.vue'),
    meta: { requiresAuth: true, permission: 'system:boss_log:view' },
  },
  {
    path: '/activity/boss',
    name: 'activity-boss',
    component: () => import('@/views/activity/BossView.vue'),
    meta: { requiresAuth: true, permission: 'activity:boss:view' },
  },
  {
    path: '/activity/boss-history',
    name: 'activity-boss-history',
    component: () => import('@/views/activity/BossHistoryView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'activity:boss:view',
      activeMenu: '/activity/boss',
    },
  },
  {
    path: '/notices',
    name: 'notices',
    component: () => import('@/views/notice/NoticesView.vue'),
    meta: { requiresAuth: true, permission: 'notice:view' },
  },
  {
    path: '/system/notices',
    name: 'system-notices',
    component: () => import('@/views/system/NoticesView.vue'),
    meta: { requiresAuth: true, permission: 'system:notice:view' },
  },
  // legacy redirects
  { path: '/system/user-points', redirect: '/reward/my-points' },
  { path: '/system/point-details', redirect: '/reward/point-details' },
  { path: '/system/lottery-ticket-details', redirect: '/reward/lottery-ticket-details' },
  {
    path: '/biz/devices',
    name: 'biz-devices',
    component: () => import('@/views/biz/DevicesView.vue'),
    meta: { requiresAuth: true, permission: 'biz:device:view' },
  },
  {
    path: '/biz/device-funcs',
    name: 'biz-device-funcs',
    component: () => import('@/views/biz/DeviceFuncsView.vue'),
    meta: { requiresAuth: true, permission: 'biz:device_func:view' },
  },
  {
    path: '/biz/device-ips',
    name: 'biz-device-ips',
    component: () => import('@/views/biz/DeviceIpsView.vue'),
    meta: { requiresAuth: true, permission: 'biz:device_ip:view' },
  },
  {
    path: '/biz/device-brands',
    name: 'biz-device-brands',
    component: () => import('@/views/biz/DeviceBrandsView.vue'),
    meta: { requiresAuth: true, permission: 'biz:device_brand:view' },
  },
  {
    path: '/biz/device-configs',
    name: 'biz-device-configs',
    component: () => import('@/views/biz/DeviceConfigsView.vue'),
    meta: { requiresAuth: true, permission: 'biz:device_config:view' },
  },
  // legacy redirects (old biz paths for non-device catalogs)
  { path: '/biz/funcs', redirect: '/system/funcs' },
  { path: '/biz/ip-blocks', redirect: '/system/ip-blocks' },
  { path: '/biz/code-configs', redirect: '/system/code-configs' },
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    // 列表→详情: 回顶; 详情→列表: 交还 keep-alive 记忆的滚动位置
    if (to.name === 'media-detail') return { top: 0 }
    if (from.name === 'media-detail' && to.name === 'media-platform') return false
    return { top: 0 }
  },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (!auth.bootstrapped) {
    await auth.fetchMe()
  }

  if (to.meta.public) {
    if (to.name === 'login' && auth.isAuthenticated) {
      return { name: 'dashboard' }
    }
    return true
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  const permission = to.meta.permission as string | undefined
  if (permission && !auth.hasPermission(permission)) {
    return { name: 'dashboard' }
  }

  const anyPermission = to.meta.anyPermission as string[] | undefined
  if (anyPermission?.length && !anyPermission.some((c) => auth.hasPermission(c))) {
    return { name: 'dashboard' }
  }

  return true
})

export default router
